import { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTenantConfig } from "../hooks/useTenantConfig";
import { usePendingCount } from "../hooks/usePendingCount";
import { useNewSince, type NewCounts } from "../hooks/useNewSince";
import { useRinascentePendingCount } from "../hooks/useRinascentePendingCount";
import { useAuth } from "../auth/useAuth";
import { canViewRdiQueue, canViewDdtQueue, canConfigure, canAdmin, canManageUsers, canUseDeleghe, canUseBsc, canUseDdt, canUseRinascente, canUseConversazioni, canWorkTickets } from "../auth/permissions";

import { generateSelfServiceMagicLink } from "../api/client";
import type { SelfServiceMagicLink, AuditFlow } from "../api/types";
import { AUDIT_FLOW_ORDER, AUDIT_FLOW_META } from "../lib/auditFlows";
import { useToast } from "../context/ToastContext";

const NAV_ITEMS = [
  { to: "/", label: "Coda di Lavoro", icon: "assignment", end: true, badge: true, section: "rdi" as const },
  { to: "/allarmi", label: "Allarmi", icon: "notifications_active", section: "allarme" as const },
  { to: "/hyper", label: "Hyperfabbricati", icon: "domain", section: "hyper" as const },
  { to: "/stats", label: "Statistiche", icon: "bar_chart" },
];

const QUEUE_ITEM = { to: "/queue", label: "Coda Output", icon: "outbox" };

const CONFIG_ITEMS = [
  { to: "/settings", label: "Configurazione", icon: "tune" },
];

/**
 * Layout a gruppi collassabili RDI/DDT — SOLO per il tenant Casadei
 * (tenant_id === "casadei"). Vedi `isCasadeiLayout` in Sidebar().
 * Voci indipendenti da NAV_ITEMS (usato solo dal layout flat): qui
 * "Coda di Lavoro" diventa "Email" e include "Agente" (verso
 * /conversazioni, assorbita qui dalla vecchia sezione top-level
 * "Conversazioni" — vedi AGENTE_ITEM, aggiunta in Sidebar() solo se
 * canUseConversazioni). Nessuna collisione con la label "Coda di Lavoro"
 * del gruppo DDT, perché sono annidate sotto due gruppi diversi.
 */
const RDI_GROUP_ITEMS = [
  { to: "/", label: "Email", icon: "assignment", end: true, badge: true, section: "rdi" as const },
  { to: "/hyper", label: "Hyperfabbricati", icon: "domain", section: "hyper" as const },
  { to: "/allarmi", label: "Allarmi", icon: "notifications_active", section: "allarme" as const },
  // Le statistiche stanno nel gruppo del proprio flusso, come per BSC,
  // Rinascente e Deleghe. Prima erano una voce unica cross-flusso: un
  // ddt_operator la vedeva, ma la pagina interrogava solo le RDI.
  { to: "/stats", label: "Statistiche", icon: "bar_chart" },
];

// Voce "Agente" del gruppo RDI (Casadei) — stessa route /conversazioni e
// stesso guard (canUseConversazioni, via showConversazioni) della vecchia
// sezione top-level "Conversazioni", ora rimossa.
const AGENTE_ITEM = { to: "/conversazioni", label: "Agente", icon: "forum" };

const DDT_GROUP_ITEMS = [
  { to: "/ddt", label: "Coda di Lavoro", icon: "local_shipping", end: true },
  { to: "/ddt/stats", label: "Statistiche", icon: "bar_chart" },
];

const MOP_GROUP_ITEMS = [
  { to: "/mop/requests", label: "Coda di Lavoro", icon: "inbox" },
  { to: "/mop", label: "Uscite", icon: "event_repeat", excludePath: "/mop/requests" },
];

const MONITORING_ITEMS = [
  { to: "/admin/email-batch", label: "Elaborazione Email", icon: "mark_email_read" },
  { to: "/admin/azure-sync", label: "Sync Azure", icon: "cloud_sync" },
];

const ADMIN_ITEMS = [
  { to: "/admin/tenants", label: "Clienti", icon: "apartment" },
  { to: "/admin/users", label: "Utenti", icon: "group" },
  { to: "/admin/costs", label: "Consumi", icon: "payments" },
  { to: "/admin/prices", label: "Listino prezzi", icon: "sell" },
];

const DELEGHE_NAV_ITEMS = [
  { to: "/deleghe", label: "Verifica Deleghe", icon: "verified_user", end: true },
  { to: "/deleghe/stats", label: "Statistiche Deleghe", icon: "analytics" },
];

const BSC_NAV_ITEMS = [
  { to: "/bsc", label: "Pratiche BSC", icon: "description", end: true },
  { to: "/bsc/queue", label: "Coda Output", icon: "outbox" },
  { to: "/bsc/stats", label: "Statistiche", icon: "bar_chart" },
  { to: "/bsc/lessons", label: "Correzioni", icon: "school" },
  // Registro attività: non più qui (era un duplicato) — ora è il gruppo
  // top-level "Registro attività" con sotto-voce "BSC", visibile a chiunque
  // veda almeno un flusso (vedi showAudit/auditGroupItems più sotto).
  { to: "/bsc/settings", label: "Configurazione", icon: "tune" },
];

const DDT_NAV_ITEMS = [
  { to: "/ddt", label: "Pratiche DDT", icon: "local_shipping", end: true },
  // Anche nel layout flat: oggi il DDT e' solo di Casadei, ma un tenant DDT
  // futuro erediterebbe la sezione senza le sue statistiche.
  { to: "/ddt/stats", label: "Statistiche DDT", icon: "bar_chart" },
];

const Rinascente_NAV_ITEMS = [
  { to: "/rinascente", label: "Fatture Rinascente", icon: "receipt_long", end: true },
  { to: "/rinascente/queue", label: "Coda Output", icon: "outbox" },
  { to: "/rinascente/stats", label: "Statistiche", icon: "bar_chart" },
  { to: "/rinascente/settings", label: "Configurazione", icon: "tune" },
];

function userInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

function TenantSwitcher() {
  const { activeTenant, tenantList, setActiveTenant } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Chiudi al click fuori
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (tenantList.length < 2) return null;

  const current = tenantList.find((t) => t.tenant_id === activeTenant);

  function handleSelect(id: string) {
    setActiveTenant(id);
    setOpen(false);
    navigate("/");
  }

  return (
    <div ref={ref} style={{ position: "relative", margin: "0.5rem 0.75rem 0" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 6,
          padding: "0.45rem 0.65rem",
          background: "rgba(255,255,255,0.1)",
          border: "1px solid rgba(255,255,255,0.2)",
          borderRadius: "var(--border-radius-lg)",
          color: "var(--indaco-white)",
          cursor: "pointer",
          fontSize: "var(--font-size-sm)",
          fontWeight: 600,
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, flexShrink: 0 }}>apartment</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {current?.name ?? activeTenant}
          </span>
        </span>
        <span className="material-symbols-outlined" style={{ fontSize: 16, flexShrink: 0 }}>
          {open ? "expand_less" : "expand_more"}
        </span>
      </button>

      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 4px)",
          left: 0,
          right: 0,
          background: "var(--indaco-white)",
          borderRadius: "var(--border-radius-lg)",
          boxShadow: "var(--shadow-lg)",
          zIndex: 100,
          overflow: "hidden",
        }}>
          {tenantList.map((t) => (
            <button
              key={t.tenant_id}
              onClick={() => handleSelect(t.tenant_id)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "0.55rem 0.85rem",
                background: t.tenant_id === activeTenant ? "var(--indaco-brand-primary-light)" : "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: "var(--font-size-sm)",
                color: t.tenant_id === activeTenant ? "var(--indaco-brand-dark)" : "var(--indaco-gray-800)",
                fontWeight: t.tenant_id === activeTenant ? 600 : 400,
                textAlign: "left",
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-brand-dark)" }}>apartment</span>
              {t.name}
              {t.tenant_id === activeTenant && (
                <span className="material-symbols-outlined" style={{ fontSize: 15, marginLeft: "auto", color: "var(--indaco-brand-dark)" }}>check</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface SidebarNavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
  excludePath?: string;
  badge?: boolean;
  section?: "rdi" | "allarme" | "hyper";
}

function isItemActive(item: SidebarNavItem, pathname: string): boolean {
  if (item.excludePath && (pathname === item.excludePath || pathname.startsWith(item.excludePath + "/"))) return false;
  return item.end ? pathname === item.to : pathname.startsWith(item.to);
}

function NavGroupLink({
  item,
  pendingCount,
  newCounts,
}: {
  item: SidebarNavItem;
  pendingCount: number;
  newCounts: NewCounts;
}) {
  const location = useLocation();
  const newCount = item.section ? newCounts[item.section] ?? 0 : 0;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) => `nav-item nav-item-nested${isActive && (!item.excludePath || isItemActive(item, location.pathname)) ? " active" : ""}`}
    >
      <span className="material-symbols-outlined">{item.icon}</span>
      <span>{item.label}</span>
      {item.badge && pendingCount > 0 && <span className="nav-badge">{pendingCount}</span>}
      {newCount > 0 && (
        <span className="nav-badge-new" title={`${newCount} nuov${newCount === 1 ? "o" : "i"} da ultima visita`}>
          +{newCount}
        </span>
      )}
    </NavLink>
  );
}

/**
 * Gruppo di navigazione collassabile (RDI / DDT). Espanso di default;
 * l'operatore può comprimerlo per ridurre l'ingombro visivo. Si riespande
 * automaticamente quando la route attiva rientra nel gruppo, anche se
 * era stato precedentemente compresso — evita che una voce attiva sparisca.
 */
function NavGroup({
  label,
  icon,
  items,
  pendingCount,
  newCounts,
}: {
  label: string;
  icon: string;
  items: SidebarNavItem[];
  pendingCount: number;
  newCounts: NewCounts;
}) {
  const location = useLocation();
  const hasActiveChild = items.some((item) => isItemActive(item, location.pathname));
  const [collapsed, setCollapsed] = useState(false);
  const open = hasActiveChild || !collapsed;

  return (
    <div className="sidebar-group">
      <button
        type="button"
        className={`sidebar-group-header${hasActiveChild ? " has-active" : ""}`}
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={open}
      >
        <span className="material-symbols-outlined">{icon}</span>
        <span className="sidebar-group-label">{label}</span>
        <span className="material-symbols-outlined sidebar-group-chevron">
          {open ? "expand_less" : "expand_more"}
        </span>
      </button>
      {open && (
        <div className="sidebar-group-items">
          {items.map((item) => (
            <NavGroupLink key={item.to} item={item} pendingCount={pendingCount} newCounts={newCounts} />
          ))}
        </div>
      )}
    </div>
  );
}

function MagicLinkDialog({ data, onClose }: { data: SelfServiceMagicLink; onClose: () => void }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const minutes = Math.round(data.expires_in_seconds / 60);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(data.magic_url);
      setCopied(true);
      toast.addToast("success", "Link copiato negli appunti");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.addToast("error", "Copia non riuscita — seleziona e copia manualmente");
    }
  }

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>
            <span className="material-symbols-outlined" style={{ fontSize: 22, marginRight: 6, verticalAlign: "middle" }}>link</span>
            Magic-link generato
          </h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <p className="text-sm text-muted" style={{ margin: "0 0 1rem" }}>
          Valido <strong>una sola volta</strong>, scade tra {minutes} minuti. Usalo subito, in un
          contesto browser pulito (nuova sessione o finestra in incognito): se in questo stesso
          browser è già presente una sessione salvata, aprirlo qui verrebbe ignorato in silenzio.
        </p>
        <div className="form-group">
          <label className="form-label">URL</label>
          <input className="form-input" readOnly value={data.magic_url} onFocus={(e) => e.target.select()} />
        </div>
        <div className="dialog-footer">
          <button className="btn btn-ghost" onClick={onClose}>Chiudi</button>
          <button className="btn btn-primary" onClick={handleCopy}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, marginRight: 4 }}>
              {copied ? "check" : "content_copy"}
            </span>
            {copied ? "Copiato" : "Copia link"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Sidebar({
  mobileOpen = false,
  onCloseMobile,
  collapsed = false,
  onToggleCollapse,
}: {
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
} = {}) {
  const { config } = useTenantConfig();
  const { user, logout, isPlatformAdmin } = useAuth();
  const toast = useToast();
  const [magicLink, setMagicLink] = useState<SelfServiceMagicLink | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);

  async function handleGenerateMagicLink() {
    setGeneratingLink(true);
    try {
      const data = await generateSelfServiceMagicLink();
      setMagicLink(data);
    } catch (e) {
      toast.addToast("error", `Errore: ${(e as Error).message}`);
    } finally {
      setGeneratingLink(false);
    }
  }
  const roles = user?.roles ?? [];
  const modules = config?.modules ?? [];
  const { count: pendingCount } = usePendingCount();
  const { counts: newCounts } = useNewSince();
  // Gate sul modulo del tenant corrente: evita /rinascente/count senza
  // X-Tenant-ID (platform_admin pre-selezione) che il BE respinge con 400.
  const { newCount: rinascenteNewCount } = useRinascentePendingCount(modules.includes("rinascente") && canUseRinascente(roles));
  // Backward compat: se modules vuoto mostra RDI (tenant pre-modules). Richiede anche il ruolo.
  const showRdi = (modules.length === 0 || modules.includes("rdi")) && canWorkTickets(roles);
  const showDeleghe = modules.includes("deleghe") && canUseDeleghe(roles);
  const showBsc = modules.includes("bsc") && canUseBsc(roles);
  const showDdt = modules.includes("ddt") && canUseDdt(roles);
  const showMop = config?.tenant_id === "casadei" && config.mop_enabled === true && canViewRdiQueue(roles);
  const showRinascente = modules.includes("rinascente") && canUseRinascente(roles);
  const showConversazioni = modules.includes("conversazioni") && canUseConversazioni(roles);
  // Item 1 (2026-07-14): visibilità granulare per-modulo sulla coda output,
  // non più un unico showQueue. Un utente che vede solo DDT (ddt_operator)
  // non vede più la coda RDI e viceversa; editor/admin/platform_admin
  // vedono entrambe, come il vecchio showQueue univoco.
  const showRdiQueue = canViewRdiQueue(roles);
  const showDdtQueue = canViewDdtQueue(roles);
  // Sottovoci del gruppo "Coda Output" (solo layout Casadei) — RDI e/o DDT a
  // seconda di cosa l'utente può vedere. Se ne vede solo una, il gruppo ha
  // comunque senso mostrarlo (l'utente capisce da cosa clicca cosa vede).
  const queueGroupItems: SidebarNavItem[] = [
    ...(showRdiQueue ? [{ to: "/queue", label: "RDI", icon: "assignment" }] : []),
    ...(showDdtQueue ? [{ to: "/ddt/queue", label: "DDT", icon: "local_shipping" }] : []),
  ];
  const showConfig = canConfigure(roles);
  const showAdmin = canAdmin(roles);
  const showManageUsers = canManageUsers(roles);
  // Gruppo collassabile "Impostazioni" (tenant-scoped): raccoglie
  // Configurazione, Elaborazione Email, Sync Azure — tutte voci il cui
  // contenuto riguarda il tenant attivo. Clienti, Consumi e Utenti NON ci
  // stanno: sono amministrazione di piattaforma (gestiscono o mostrano dati
  // cross-tenant), quindi vivono a un livello superiore (vedi sezione
  // "Piattaforma" più sotto — collocazione provvisoria, da rivedere). Ogni
  // voce mantiene lo stesso guard individuale che aveva da sola prima del
  // pacchetto che ha introdotto questo gruppo; il gruppo compare solo se
  // almeno una voce e' visibile. Il Registro attività NON e' più qui: è un
  // gruppo top-level a parte (vedi auditGroupItems più sotto), con una
  // sotto-voce per flusso invece di un unico link a /audit.
  const showMonitoring = showRdi && showConfig;
  function buildSettingsGroupItems(showConfigItem: boolean): SidebarNavItem[] {
    return [
      ...(showConfigItem ? CONFIG_ITEMS : []),
      ...(showMonitoring ? MONITORING_ITEMS : []),
    ];
  }
  // Gruppo "Registro attività": una sotto-voce per ogni flusso che il tenant
  // ha davvero (modulo attivo + ruolo, stessi guard delle sezioni sopra),
  // più "Piattaforma" solo per il platform_admin (eventi trasversali, non un
  // modulo di tenant). Il gate e' "ha almeno un flusso visibile" — prima era
  // showRdi || showDdt, che escludeva un tenant con solo conversazioni o
  // solo BSC dal registro attività: difetto corretto da questo pacchetto.
  const auditFlowVisibility: Record<AuditFlow, boolean> = {
    rdi: showRdi,
    ddt: showDdt,
    bsc: showBsc,
    deleghe: showDeleghe,
    rinascente: showRinascente,
    conversazioni: showConversazioni,
    piattaforma: isPlatformAdmin,
  };
  const auditFlowSubItems: SidebarNavItem[] = AUDIT_FLOW_ORDER
    .filter((flow) => auditFlowVisibility[flow])
    .map((flow) => ({
      to: `/audit/${flow}`,
      label: AUDIT_FLOW_META[flow].label,
      icon: AUDIT_FLOW_META[flow].icon,
      end: true,
    }));
  const showAudit = auditFlowSubItems.length > 0;
  const auditGroupItems: SidebarNavItem[] = showAudit
    ? [{ to: "/audit", label: "Tutti i flussi", icon: "apps", end: true }, ...auditFlowSubItems]
    : [];
  // Il guard di "Configurazione" cambia leggermente tra i due layout,
  // comportamento preesistente e invariato da questo pacchetto: nel layout
  // Casadei anche showDdt da solo la rende visibile, nel layout flat no.
  const adminGroupItemsCasadei = buildSettingsGroupItems((showRdi || showDdt || showConversazioni) && showConfig);
  const adminGroupItemsFlat = buildSettingsGroupItems((showRdi || showConversazioni) && showConfig);
  // Layout a gruppi RDI/DDT collassabili — SOLO per il tenant Casadei.
  // Nessun altro tenant è affetto: per tutti gli altri il ramo else
  // qui sotto riproduce esattamente il layout flat preesistente.
  const isCasadeiLayout = config?.tenant_id === "casadei";
  // Voci del gruppo RDI (Casadei): "Agente" (ex sezione top-level
  // "Conversazioni") va subito dopo "Email", solo se il tenant ha il
  // modulo conversazioni attivo e l'utente ha il permesso.
  const rdiGroupItems: SidebarNavItem[] = showConversazioni
    ? [RDI_GROUP_ITEMS[0], AGENTE_ITEM, ...RDI_GROUP_ITEMS.slice(1)]
    : RDI_GROUP_ITEMS;
  // Stesso assorbimento della vecchia sezione top-level "Conversazioni",
  // ma per il layout flat (tenant non-Casadei): senza questo, un futuro
  // tenant non-Casadei con modulo conversazioni attivo perderebbe ogni
  // punto di accesso a /conversazioni dal menu.
  const flatRdiItems: SidebarNavItem[] = showConversazioni
    ? [NAV_ITEMS[0], AGENTE_ITEM, ...NAV_ITEMS.slice(1)]
    : NAV_ITEMS;

  return (
    <aside className={`sidebar${mobileOpen ? " open" : ""}`}>
      {/* Chiudi drawer (solo mobile) */}
      <button
        type="button"
        className="sidebar-close"
        aria-label="Chiudi menu"
        onClick={onCloseMobile}
      >
        <span className="material-symbols-outlined">close</span>
      </button>
      {/* Collassa/espandi la sidebar su desktop (nascosto sotto il breakpoint
          mobile, dove la sidebar e' gia' un drawer). */}
      {onToggleCollapse && (
        <button
          type="button"
          className="sidebar-collapse-toggle"
          onClick={onToggleCollapse}
          title={collapsed ? "Espandi il menu" : "Comprimi il menu (solo icone)"}
          aria-label={collapsed ? "Espandi il menu" : "Comprimi il menu"}
          aria-expanded={!collapsed}
        >
          <span className="material-symbols-outlined">
            {collapsed ? "chevron_right" : "chevron_left"}
          </span>
        </button>
      )}
      {/* Branding */}
      <div className="sidebar-brand">
        <img src="/tinexta-logo-white.svg" alt="Tinexta InfoCert" className="brand-logo" />
        <div className="product-row">
          <img src="/trusty-icon-white.svg" alt="" />
          <span className="product-name">Trusty Sieve</span>
        </div>
        {config && <div className="product-sub">{config.name}</div>}
      </div>
      <TenantSwitcher />

      {/* Navigation */}
      <nav className="sidebar-nav">
        {isCasadeiLayout ? (
          <>
            {/* ── RDI e DDT come gruppi collassabili (solo tenant Casadei) ── */}
            {showRdi && (
              <NavGroup label="RDI" icon="assignment" items={rdiGroupItems} pendingCount={pendingCount} newCounts={newCounts} />
            )}
            {showDdt && (
              <NavGroup label="DDT" icon="local_shipping" items={DDT_GROUP_ITEMS} pendingCount={0} newCounts={newCounts} />
            )}
            {showMop && (
              <NavGroup label="MOP" icon="event_repeat" items={MOP_GROUP_ITEMS} pendingCount={0} newCounts={newCounts} />
            )}

            {/* ── Voci condivise cross-flusso (RDI + DDT) ── */}
            {/* "Statistiche" NON e' piu' qui: ogni flusso ha la propria dentro
                il proprio gruppo, perche' i numeri di un flusso non riguardano
                chi lavora l'altro. */}
            {(showRdi || showDdt) && queueGroupItems.length > 0 && (
              <NavGroup label="Coda Output" icon="outbox" items={queueGroupItems} pendingCount={0} newCounts={newCounts} />
            )}
            {/* Gruppo "Impostazioni" (tenant-scoped) — vedi adminGroupItemsCasadei
                più sopra per i guard individuali di ciascuna voce. Clienti/Consumi/
                Utenti NON sono qui, vedi sezione "Piattaforma" in fondo alla sidebar. */}
            {adminGroupItemsCasadei.length > 0 && (
              <NavGroup label="Impostazioni" icon="tune" items={adminGroupItemsCasadei} pendingCount={0} newCounts={newCounts} />
            )}
            {/* Gruppo "Registro attività" — una sotto-voce per flusso (vedi
                auditGroupItems più sopra). Top-level, non annidato in
                "Impostazioni": riguarda tutti i flussi, non la
                configurazione di uno solo. */}
            {showAudit && (
              <NavGroup label="Registro attività" icon="history" items={auditGroupItems} pendingCount={0} newCounts={newCounts} />
            )}
          </>
        ) : (
          <>
            {/* ── Sezione RDI (layout flat preesistente — tutti gli altri tenant) ── */}
            {showRdi && flatRdiItems.map((item) => {
              const newCount = "section" in item && item.section ? newCounts[item.section] : 0;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
                >
                  <span className="material-symbols-outlined">{item.icon}</span>
                  <span>{item.label}</span>
                  {"badge" in item && item.badge && pendingCount > 0 && (
                    <span className="nav-badge">{pendingCount}</span>
                  )}
                  {newCount > 0 && (
                    <span className="nav-badge-new" title={`${newCount} nuov${newCount === 1 ? "o" : "i"} da ultima visita`}>
                      +{newCount}
                    </span>
                  )}
                </NavLink>
              );
            })}

            {showRdi && showRdiQueue && (
              <NavLink to={QUEUE_ITEM.to} className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}>
                <span className="material-symbols-outlined">{QUEUE_ITEM.icon}</span>
                <span>{QUEUE_ITEM.label}</span>
              </NavLink>
            )}

            {/* Gruppo "Impostazioni" (tenant-scoped) — stesse voci del layout
                Casadei, stessi guard individuali (vedi adminGroupItemsFlat più
                sopra). Il guard di "Configurazione" qui è
                (showRdi || showConversazioni), senza showDdt: comportamento
                preesistente, invariato. */}
            {adminGroupItemsFlat.length > 0 && (
              <NavGroup label="Impostazioni" icon="tune" items={adminGroupItemsFlat} pendingCount={0} newCounts={newCounts} />
            )}
            {/* Gruppo "Registro attività" — stesso gruppo del layout Casadei,
                deve funzionare anche nel layout piatto (NavGroup/.sidebar-group
                non sono scoped a un layout). */}
            {showAudit && (
              <NavGroup label="Registro attività" icon="history" items={auditGroupItems} pendingCount={0} newCounts={newCounts} />
            )}
          </>
        )}

        {/* ── Sezione Deleghe (Acquirente Unico) ── */}
        {showDeleghe && (
          <>
            <div className="sidebar-section-label">Deleghe</div>
            {DELEGHE_NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={"end" in item ? item.end : undefined}
                className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
              >
                <span className="material-symbols-outlined">{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}

        {/* ── Sezione BSC (Frabemar) ── */}
        {showBsc && (
          <>
            <div className="sidebar-section-label">BSC</div>
            {BSC_NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={"end" in item ? item.end : undefined}
                className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
              >
                <span className="material-symbols-outlined">{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}

        {/* ── Sezione DDT flat (tenant non-Casadei con modulo ddt) ──
            Per Casadei il DDT è già reso sopra come NavGroup collassabile. */}
        {!isCasadeiLayout && showDdt && (
          <>
            <div className="sidebar-section-label">DDT</div>
            {DDT_NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={"end" in item ? item.end : undefined}
                className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
              >
                <span className="material-symbols-outlined">{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}

        {/* ── Sezione Rinascente / La Rinascente ── */}
        {showRinascente && (
          <>
            <div className="sidebar-section-label">Rinascente</div>
            {Rinascente_NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={"end" in item ? item.end : undefined}
                className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
              >
                <span className="material-symbols-outlined">{item.icon}</span>
                <span>{item.label}</span>
                {item.end && rinascenteNewCount > 0 && (
                  <span
                    className="nav-badge-new"
                    title={`${rinascenteNewCount} nuov${rinascenteNewCount === 1 ? "a" : "e"} da ultima visita`}
                  >
                    +{rinascenteNewCount}
                  </span>
                )}
              </NavLink>
            ))}
          </>
        )}

        {/* La sezione top-level "Conversazioni" e' stata rimossa: assorbita
            come voce "Agente" nel gruppo RDI del layout Casadei (vedi
            AGENTE_ITEM/rdiGroupItems), stesso guard showConversazioni. */}

        {/* ── Piattaforma (cross-tenant, condivisa tra i due layout) ──
            Clienti, Consumi, Listino prezzi e Utenti non sono nel gruppo "Impostazioni"
            sopra: riguardano tutti i tenant (Clienti li amministra, Consumi
            e Utenti mostrano/gestiscono dati cross-tenant), non solo quello
            attivo — restano quindi a un livello superiore, separati
            visivamente. Collocazione provvisoria (segnalato dall'utente
            2026-09-04): da rivedere dove posizionarle definitivamente.
            Ogni voce mantiene il proprio guard individuale, il label
            compare se almeno una e' visibile. */}
        {(showAdmin || showManageUsers) && (
          <>
            <div className="sidebar-section-label">Piattaforma</div>
            {showAdmin && (
              <NavLink to={ADMIN_ITEMS[0].to} className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}>
                <span className="material-symbols-outlined">{ADMIN_ITEMS[0].icon}</span>
                <span>{ADMIN_ITEMS[0].label}</span>
              </NavLink>
            )}
            {showManageUsers && (
              <NavLink to={ADMIN_ITEMS[1].to} className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}>
                <span className="material-symbols-outlined">{ADMIN_ITEMS[1].icon}</span>
                <span>{ADMIN_ITEMS[1].label}</span>
              </NavLink>
            )}
            {showAdmin && (
              <NavLink to={ADMIN_ITEMS[2].to} className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}>
                <span className="material-symbols-outlined">{ADMIN_ITEMS[2].icon}</span>
                <span>{ADMIN_ITEMS[2].label}</span>
              </NavLink>
            )}
            {showAdmin && (
              <NavLink to={ADMIN_ITEMS[3].to} className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}>
                <span className="material-symbols-outlined">{ADMIN_ITEMS[3].icon}</span>
                <span>{ADMIN_ITEMS[3].label}</span>
              </NavLink>
            )}
          </>
        )}
      </nav>

      {/* User info */}
      {user && (
        <div className="sidebar-user">
          <div className="flex items-center">
            <span className="user-avatar">{userInitials(user.name)}</span>
            <div style={{ minWidth: 0 }}>
              <div className="user-name truncate">{user.name}</div>
              <div className="user-role">{user.roles?.[0] ?? ""}</div>
            </div>
          </div>
          {canAdmin(roles) && (
            <button
              className="btn btn-sm btn-on-dark"
              onClick={handleGenerateMagicLink}
              disabled={generatingLink}
              title="Genera un magic-link on-demand per il tuo account (login rapido o hand-off a un agente di test)"
              style={{ width: "100%", justifyContent: "flex-start", gap: "var(--space-2)", marginTop: "var(--space-2)" }}
            >
              {generatingLink
                ? <span className="spinner" style={{ width: 14, height: 14 }} />
                : <span className="material-symbols-outlined" style={{ fontSize: 16 }}>link</span>}
              <span>Genera magic-link</span>
            </button>
          )}
          <button className="btn-logout" onClick={logout} title="Esci">
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>logout</span>
            <span>Esci</span>
          </button>
        </div>
      )}
      {magicLink && <MagicLinkDialog data={magicLink} onClose={() => setMagicLink(null)} />}
    </aside>
  );
}
